// ============================================================
// api/invitations.js
// Endpoint bindings for the four mgmt/invitations endpoints.
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-invitations-list.js
//   backend/func-carehub-api/src/functions/admin-invitations-issue.js
//   backend/func-carehub-api/src/functions/admin-invitations-revoke.js
//   backend/func-carehub-api/src/functions/admin-invitations-resend.js
//   APIM operations: mgmt-invitations-list, mgmt-invitations-issue,
//                    mgmt-invitations-revoke, mgmt-invitations-resend
// ============================================================

import { apiGet, apiPost } from './client';

/**
 * List invitations with optional filters and pagination.
 *
 * Stored `pending` rows past their `expiresAt` are surfaced as
 * `'expired'` in the response (computed at query time; see
 * admin-invitations-list.js header for the rationale).
 *
 * @param {Object} [params={}]
 * @param {number}                                                       [params.clientId]
 * @param {('pending'|'redeemed'|'revoked'|'expired')}                   [params.status]
 * @param {number}                                                       [params.limit=50]    1..200, silently clamped
 * @param {number}                                                       [params.offset=0]
 *
 * @returns {Promise<{
 *   invitations: Array<{
 *     invitationId: number,
 *     clientId: number,
 *     clientName: string,
 *     email: string,
 *     displayName: string|null,
 *     roleKey: 'manager'|'carer'|'admin'|'auditor',
 *     status: 'pending'|'redeemed'|'revoked'|'expired',
 *     expiresAt: string|null,
 *     createdAt: string|null,
 *     lastReminderAt: string|null,   // reminder timer / manual resend stamp
 *     reminderCount: number,         // automatic reminders only
 *   }>,
 *   pagination: { limit: number, offset: number, returned: number }
 * }>}
 */
export async function listInvitations(params = {}) {
  return apiGet('/mgmt/invitations', params);
}

/**
 * Issue a new invitation. Generates a token, persists the
 * Invitations row (Status=pending, expires in 30 days — the staff
 * lifetime since 2026-09-03; see backend shared/invitation-send.js),
 * and sends the branded invitation email via ACS.
 *
 * Failure modes worth distinguishing in the UI (via ApiError.status):
 *   400  — validation (check ApiError.field for the offending key)
 *   409  — a pending invitation already exists for (clientId, email)
 *          (response body includes existingInvitationId)
 *   500  — INSERT failed (no row), OR INSERT succeeded but ACS send
 *          failed (response body includes invitationId — the row is
 *          Pending; admin can revoke + re-issue)
 *
 * @param {Object} body
 * @param {number}                                       body.clientId
 * @param {string}                                       body.email
 * @param {string}                                       body.displayName
 * @param {('manager'|'carer'|'admin'|'auditor')}        body.roleKey
 * @param {Object}                                       [body.metadata]   Forward-compat JSON object.
 *
 * @returns {Promise<{
 *   invitationId: number,
 *   clientId: number,
 *   email: string,
 *   displayName: string,
 *   roleKey: string,
 *   expiresAt: string,
 *   status: 'Pending'
 * }>}
 */
export async function issueInvitation(body) {
  return apiPost('/mgmt/invitations/issue', body);
}

/**
 * Revoke a pending invitation. Idempotent — re-revoking returns
 * `wasIdempotent: true` with no fresh audit row and no DB change.
 * Revoking a redeemed invitation returns 409 (deactivate the
 * ClientUsers row instead).
 *
 * @param {number}      invitationId         Positive integer; throws TypeError if not.
 * @param {string|null} [reason=null]        Optional reason; only sent if non-null. Backend cap 500 chars.
 *
 * @returns {Promise<{
 *   invitationId: number,
 *   status: 'revoked',
 *   revokedAt: string|null,
 *   wasIdempotent: boolean
 * }>}
 */
export async function revokeInvitation(invitationId, reason = null) {
  if (!Number.isInteger(invitationId) || invitationId <= 0) {
    throw new TypeError(
      `revokeInvitation: invitationId must be a positive integer, got: ${JSON.stringify(invitationId)}`
    );
  }
  const body = reason != null ? { reason } : {};
  return apiPost(`/mgmt/invitations/${invitationId}/revoke`, body);
}

/**
 * Re-send the branded email for an existing PENDING invitation.
 * Rotates the token + hash (the previously-emailed link dies),
 * bumps ExpiresAt to now+30d, stamps LastReminderAt so the 7-day
 * reminder timer measures from this send, and re-sends via ACS. A
 * stored-pending row past its computed expiry is resendable — that
 * is the point. Consumers: the Invitations page row action, the
 * Client detail Users-tab pending strip, and ManualInviteBanner.
 *
 * Failure modes (via ApiError.status):
 *   409 — not_pending (redeemed/revoked), or a concurrent-change race
 *   500 — email send failed AFTER the token rotation; the old link is
 *         already dead — retry the resend
 *
 * @param {number} invitationId  Positive integer; throws TypeError if not.
 * @returns {Promise<{
 *   invitationId: number, clientId: number, email: string,
 *   status: 'pending', expiresAt: string, resent: true
 * }>}
 */
export async function resendInvitation(invitationId) {
  if (!Number.isInteger(invitationId) || invitationId <= 0) {
    throw new TypeError(
      `resendInvitation: invitationId must be a positive integer, got: ${JSON.stringify(invitationId)}`
    );
  }
  return apiPost(`/mgmt/invitations/${invitationId}/resend`, {});
}
